import { useLanguage } from '../context/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Separator } from '../components/ui/separator';

const LegalPage = () => {
  const { t, language } = useLanguage();

  return (
    <div className="min-h-screen py-16 px-4 md:px-8">
      <div className="max-w-4xl mx-auto">
        <h1 className="font-heading text-3xl md:text-4xl font-bold mb-8 text-center" data-testid="legal-title">
          {t('legalTitle')}
        </h1>

        <div className="space-y-8">
          {/* CGU */}
          <Card id="cgu">
            <CardHeader>
              <CardTitle className="font-heading text-xl">{t('cguTitle')}</CardTitle>
            </CardHeader>
            <CardContent className="prose prose-sm max-w-none text-muted-foreground">
              {language === 'fr' ? (
                <>
                  <h3 className="text-foreground font-semibold">1. Objet</h3>
                  <p>
                    Les présentes Conditions Générales d'Utilisation (CGU) régissent l'utilisation de la plateforme Roomia.net, 
                    accessible à l'adresse roomia.net. En utilisant notre plateforme, vous acceptez ces conditions.
                  </p>

                  <h3 className="text-foreground font-semibold mt-6">2. Description du service</h3>
                  <p>
                    Roomia.net est une plateforme de mise en relation entre propriétaires de logements meublés et personnes 
                    à la recherche d'hébergement au Cameroun et en Afrique. Roomia.net agit uniquement en tant qu'intermédiaire 
                    et ne possède ni ne gère directement les logements listés sur la plateforme.
                  </p>

                  <h3 className="text-foreground font-semibold mt-6">3. Responsabilités</h3>
                  <p>
                    <strong>Roomia.net ne garantit pas :</strong>
                  </p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>L'exactitude des informations fournies par les propriétaires</li>
                    <li>La qualité, la sécurité ou la légalité des logements proposés</li>
                    <li>La capacité des propriétaires à louer leurs biens</li>
                    <li>La solvabilité des utilisateurs</li>
                  </ul>
                  <p className="mt-4">
                    <strong>Les utilisateurs sont responsables de :</strong>
                  </p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Vérifier les informations avant toute transaction</li>
                    <li>Visiter les logements avant de s'engager</li>
                    <li>Conclure des contrats de location appropriés</li>
                    <li>Respecter les lois et réglementations locales</li>
                  </ul>

                  <h3 className="text-foreground font-semibold mt-6">4. Publication d'annonces</h3>
                  <p>
                    Les propriétaires s'engagent à fournir des informations exactes et à jour. Roomia.net se réserve le droit 
                    de supprimer toute annonce ne respectant pas ces conditions ou les lois en vigueur.
                  </p>

                  <h3 className="text-foreground font-semibold mt-6">5. Contact via WhatsApp</h3>
                  <p>
                    La communication entre utilisateurs se fait via WhatsApp. Roomia.net n'est pas responsable des échanges 
                    et transactions effectués en dehors de la plateforme.
                  </p>

                  <h3 className="text-foreground font-semibold mt-6">6. Limitation de responsabilité</h3>
                  <p>
                    Roomia.net ne pourra être tenu responsable de tout dommage direct ou indirect résultant de l'utilisation 
                    de la plateforme, des annonces publiées ou des transactions effectuées entre utilisateurs.
                  </p>

                  <h3 className="text-foreground font-semibold mt-6">7. Modification des CGU</h3>
                  <p>
                    Roomia.net se réserve le droit de modifier ces CGU à tout moment. Les utilisateurs seront informés 
                    de toute modification significative.
                  </p>
                </>
              ) : (
                <>
                  <h3 className="text-foreground font-semibold">1. Purpose</h3>
                  <p>
                    These Terms of Service govern the use of the Roomia.net platform, accessible at roomia.net. 
                    By using our platform, you agree to these terms.
                  </p>

                  <h3 className="text-foreground font-semibold mt-6">2. Service Description</h3>
                  <p>
                    Roomia.net is a platform connecting furnished accommodation owners with people looking for housing 
                    in Cameroon and Africa. Roomia.net acts solely as an intermediary and does not own or directly 
                    manage the properties listed on the platform.
                  </p>

                  <h3 className="text-foreground font-semibold mt-6">3. Responsibilities</h3>
                  <p>
                    <strong>Roomia.net does not guarantee:</strong>
                  </p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>The accuracy of information provided by owners</li>
                    <li>The quality, safety or legality of proposed accommodations</li>
                    <li>The ability of owners to rent their properties</li>
                    <li>The solvency of users</li>
                  </ul>
                  <p className="mt-4">
                    <strong>Users are responsible for:</strong>
                  </p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Verifying information before any transaction</li>
                    <li>Visiting accommodations before committing</li>
                    <li>Concluding appropriate rental contracts</li>
                    <li>Respecting local laws and regulations</li>
                  </ul>

                  <h3 className="text-foreground font-semibold mt-6">4. Publishing Listings</h3>
                  <p>
                    Owners agree to provide accurate and up-to-date information. Roomia.net reserves the right 
                    to remove any listing that does not comply with these conditions or applicable laws.
                  </p>

                  <h3 className="text-foreground font-semibold mt-6">5. Contact via WhatsApp</h3>
                  <p>
                    Communication between users is done via WhatsApp. Roomia.net is not responsible for exchanges 
                    and transactions made outside the platform.
                  </p>

                  <h3 className="text-foreground font-semibold mt-6">6. Limitation of Liability</h3>
                  <p>
                    Roomia.net cannot be held responsible for any direct or indirect damage resulting from the use 
                    of the platform, published listings, or transactions between users.
                  </p>

                  <h3 className="text-foreground font-semibold mt-6">7. Modification of Terms</h3>
                  <p>
                    Roomia.net reserves the right to modify these Terms at any time. Users will be notified 
                    of any significant changes.
                  </p>
                </>
              )}
            </CardContent>
          </Card>

          <Separator />

          {/* Privacy Policy */}
          <Card id="privacy">
            <CardHeader>
              <CardTitle className="font-heading text-xl">{t('privacyTitle')}</CardTitle>
            </CardHeader>
            <CardContent className="prose prose-sm max-w-none text-muted-foreground">
              {language === 'fr' ? (
                <>
                  <h3 className="text-foreground font-semibold">1. Collecte des données</h3>
                  <p>
                    Roomia.net collecte les données suivantes lors de votre inscription et utilisation :
                  </p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Nom et prénom</li>
                    <li>Adresse email</li>
                    <li>Numéro de téléphone (WhatsApp)</li>
                    <li>Informations sur les annonces publiées</li>
                    <li>Données de connexion (Google OAuth si utilisé)</li>
                  </ul>

                  <h3 className="text-foreground font-semibold mt-6">2. Utilisation des données</h3>
                  <p>Vos données sont utilisées pour :</p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Gérer votre compte utilisateur</li>
                    <li>Permettre la publication et la recherche d'annonces</li>
                    <li>Faciliter la communication entre utilisateurs</li>
                    <li>Améliorer nos services</li>
                    <li>Vous envoyer des notifications importantes</li>
                  </ul>

                  <h3 className="text-foreground font-semibold mt-6">3. Partage des données</h3>
                  <p>
                    Roomia.net ne vend pas vos données personnelles. Vos informations de contact sont partagées 
                    uniquement avec les utilisateurs qui souhaitent vous contacter concernant une annonce.
                  </p>

                  <h3 className="text-foreground font-semibold mt-6">4. Sécurité</h3>
                  <p>
                    Nous mettons en œuvre des mesures de sécurité appropriées pour protéger vos données 
                    contre tout accès non autorisé, modification ou divulgation.
                  </p>

                  <h3 className="text-foreground font-semibold mt-6">5. Vos droits</h3>
                  <p>Vous avez le droit de :</p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Accéder à vos données personnelles</li>
                    <li>Corriger vos données</li>
                    <li>Supprimer votre compte et vos données</li>
                    <li>Retirer votre consentement à tout moment</li>
                  </ul>

                  <h3 className="text-foreground font-semibold mt-6">6. Contact</h3>
                  <p>
                    Pour toute question concernant vos données personnelles, contactez-nous à : 
                    privacy@roomia.net ou via WhatsApp au +237 682 329 126.
                  </p>
                </>
              ) : (
                <>
                  <h3 className="text-foreground font-semibold">1. Data Collection</h3>
                  <p>
                    Roomia.net collects the following data during your registration and use:
                  </p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>First and last name</li>
                    <li>Email address</li>
                    <li>Phone number (WhatsApp)</li>
                    <li>Information about published listings</li>
                    <li>Connection data (Google OAuth if used)</li>
                  </ul>

                  <h3 className="text-foreground font-semibold mt-6">2. Use of Data</h3>
                  <p>Your data is used to:</p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Manage your user account</li>
                    <li>Enable publishing and searching listings</li>
                    <li>Facilitate communication between users</li>
                    <li>Improve our services</li>
                    <li>Send you important notifications</li>
                  </ul>

                  <h3 className="text-foreground font-semibold mt-6">3. Data Sharing</h3>
                  <p>
                    Roomia.net does not sell your personal data. Your contact information is shared only 
                    with users who wish to contact you about a listing.
                  </p>

                  <h3 className="text-foreground font-semibold mt-6">4. Security</h3>
                  <p>
                    We implement appropriate security measures to protect your data against 
                    unauthorized access, modification or disclosure.
                  </p>

                  <h3 className="text-foreground font-semibold mt-6">5. Your Rights</h3>
                  <p>You have the right to:</p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Access your personal data</li>
                    <li>Correct your data</li>
                    <li>Delete your account and data</li>
                    <li>Withdraw your consent at any time</li>
                  </ul>

                  <h3 className="text-foreground font-semibold mt-6">6. Contact</h3>
                  <p>
                    For any questions regarding your personal data, contact us at: 
                    privacy@roomia.net or via WhatsApp at +237 682 329 126.
                  </p>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default LegalPage;
